/**
 * Cognito + Azure AD 認証モジュール (Implicit Grant)
 */

const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN;
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;
const REDIRECT_URI = process.env.REACT_APP_COGNITO_REDIRECT_URI;
const AUTH_DISABLED = process.env.REACT_APP_AUTH_DISABLED === 'true';

const TOKEN_KEY = 'mobibi_access_token';
const TOKEN_EXPIRY_KEY = 'mobibi_token_expiry';
const AUTH_FAILED_KEY = 'mobibi_auth_failed';
const USER_EMAIL_KEY = 'mobibi_user_email';
const DEV_LOGIN_KEY = 'mobibi_dev_login';

// 開発者ログイン画面のパス（MSを介さず ID+パスワードでログインする）
export const SYSTEM_ADMIN_ROUTE = '/system-admin';

export function isSystemAdminRoute() {
	return window.location.pathname === SYSTEM_ADMIN_ROUTE;
}

export function isDevLogin() {
	return sessionStorage.getItem(DEV_LOGIN_KEY) === 'true';
}

export function setDevLogin() {
	sessionStorage.setItem(DEV_LOGIN_KEY, 'true');
}

export function redirectToSystemAdmin() {
	window.location.href = SYSTEM_ADMIN_ROUTE;
}

export function isAuthDisabled() {
	// 明示的に無効化、または Cognito 設定が未定義の場合はスキップ
	return AUTH_DISABLED || !COGNITO_DOMAIN || !CLIENT_ID;
}

export function isAuthFailed() {
	return sessionStorage.getItem(AUTH_FAILED_KEY) === 'true';
}

export function setAuthFailed() {
	sessionStorage.setItem(AUTH_FAILED_KEY, 'true');
}

export function getUserEmail() {
	return sessionStorage.getItem(USER_EMAIL_KEY) || '';
}

export function getUserName() {
	return sessionStorage.getItem('mobibi_user_name') || '';
}

export function getToken() {
	const token = sessionStorage.getItem(TOKEN_KEY);
	const expiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
	if (!token || !expiry) return null;
	if (Date.now() >= Number(expiry)) {
		clearToken();
		return null;
	}
	return token;
}

export function setToken(accessToken, expiresIn) {
	const expiry = Date.now() + expiresIn * 1000 - 60000;
	sessionStorage.setItem(TOKEN_KEY, accessToken);
	sessionStorage.setItem(TOKEN_EXPIRY_KEY, String(expiry));
	sessionStorage.removeItem(AUTH_FAILED_KEY);
}

export function clearToken() {
	sessionStorage.removeItem(TOKEN_KEY);
	sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
	sessionStorage.removeItem(USER_EMAIL_KEY);
	sessionStorage.removeItem('mobibi_user_name');
	sessionStorage.removeItem('mobibi_user_permissions');
}

function decodeJwtPayload(token) {
	try {
		const parts = token.split('.');
		if (parts.length !== 3) return null;
		const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
		return JSON.parse(atob(payload));
	} catch {
		return null;
	}
}

export function parseCallbackHash() {
	const hash = window.location.hash;
	if (!hash || !hash.includes('access_token=')) return null;
	const params = new URLSearchParams(hash.substring(1));
	const accessToken = params.get('access_token');
	const idToken = params.get('id_token');
	const expiresIn = params.get('expires_in');
	if (!accessToken) return null;

	let email = '';
	if (idToken) {
		const claims = decodeJwtPayload(idToken);
		if (claims) {
			email = claims.email || '';
		}
	}

	return { access_token: accessToken, expires_in: expiresIn, email };
}

export function redirectToLogin() {
	// MS(Cognito) ログインへ切り替えるため、開発者ログインの状態は破棄する
	sessionStorage.removeItem(DEV_LOGIN_KEY);
	const url =
		`${COGNITO_DOMAIN}/oauth2/authorize` +
		`?identity_provider=AzureAD` +
		`&client_id=${CLIENT_ID}` +
		`&response_type=TOKEN` +
		// profile を要求しないと userInfo が name を返さず、氏名が取得できない
		`&scope=openid%20email%20profile` +
		`&redirect_uri=${encodeURIComponent(REDIRECT_URI)}`;
	window.location.href = url;
}

export function handleAuthCallback() {
	const result = parseCallbackHash();
	if (!result) return false;
	setToken(result.access_token, parseInt(result.expires_in, 10) || 86400);
	if (result.email) {
		sessionStorage.setItem(USER_EMAIL_KEY, result.email);
	}
	window.history.replaceState(
		{},
		'',
		window.location.pathname + window.location.search
	);
	return true;
}

export function getTokenExpiry() {
	const expiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
	return expiry ? Number(expiry) : 0;
}

export function ensureAuth() {
	if (isAuthDisabled()) return 'authenticated';
	// 意図的に /system-admin を開いたときは、既存セッションより優先して
	// 開発者ログイン画面を表示する（MSにも飛ばさない）
	if (isSystemAdminRoute()) return 'devlogin';
	if (handleAuthCallback()) return 'callback';
	if (getToken()) return 'authenticated';
	// トークンなし or 期限切れ → 常にログインへリダイレクト
	// (以前の AUTH_FAILED フラグで止まっていた問題を修正)
	sessionStorage.removeItem(AUTH_FAILED_KEY);
	redirectToLogin();
	return 'redirecting';
}
